import { MenuProps } from 'antd'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { MoreOutlined } from '@ant-design/icons'

import { useStore } from 'store/useStore'
import { ItemBox } from 'sections/LKSection/shared/components/UI/ItemBox/ItemBox'
import { Status, StatusUIType } from 'sections/LKSection/shared/components/UI/Status/Status'
import { Dropdown, Flex, Text } from 'components/uiKit'
import {
  getEmployeePageRoute,
  getMovingPvzPageRoute,
  getPvzItemRoute,
  getPvzSignboardRoute,
} from 'utils/helpers/routes/getRoutes'
import { useResize } from 'utils/hooks/useResize'
import { colors, DATE_FORMAT_DEFAULT } from 'shared/constants/values'

import { RatingBadge } from '../../../../shared/components/RatingBadge/RatingBadge'
import { PvzKeys } from '../../data'
import { Pvz } from '../../stores/entity/Pvz'

import s from './PvzItemHeader.module.scss'

const getKey = (key: string) => `${PvzKeys.pvzPage}.${key}`

type PvzItemHeaderProps = {
  pvz: Pvz
}

export const PvzItemHeader = observer(({ pvz }: PvzItemHeaderProps) => {
  const { viewPvzUiStore: uiStore } = useStore()

  const { isTablet } = useResize()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const isEmployee = pvz.id ? uiStore.isEmployee(pvz.id) : false
  const isOwner = pvz.id ? uiStore.isOwner(pvz.id) : false

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: 'Сотрудники',
      onClick: () => {
        if (pvz.id) {
          navigate(getEmployeePageRoute(+pvz.id, uiStore.page, uiStore.pageSize))
        }
      },
    },
    {
      key: '2',
      label: 'Вывеска',
      onClick: () => {
        if (pvz.id) {
          navigate(getPvzSignboardRoute(+pvz.id, uiStore.page, uiStore.pageSize))
        }
      },
    },
  ]

  if (isOwner) {
    items.push({
      key: '3',
      label: 'Переезд',
      onClick: () => {
        if (pvz.id) {
          navigate(getMovingPvzPageRoute(+pvz.id))
        }
      },
    })
  }

  const pvzItemClickHandler = () => {
    if (pvz.id) {
      uiStore.setSelectedPvz(pvz)
      navigate(getPvzItemRoute(+pvz.id, uiStore.page, uiStore.pageSize))
    }
  }

  return (
    <ItemBox onClick={pvzItemClickHandler}>
      <Flex
        align={isTablet ? 'flex-start' : 'center'}
        justify="space-between"
        gap={isTablet ? 16 : 40}
        vertical={isTablet}
        style={{ width: '100%' }}
      >
        <Flex
          gap={16}
          align={isTablet ? 'flex-start' : 'center'}
          justify={isTablet ? 'space-between' : 'flex-start'}
          style={{ width: '100%' }}
        >
          <Flex className={s.statusWrap} gap={8} align="flex-start" vertical>
            <Flex vertical align="center" gap={8}>
              <Status
                title={pvz.isActive ? t(getKey('Активен')) : t(getKey('Закрыт'))}
                type={pvz.isActive ? StatusUIType.green : StatusUIType.red}
              />
            </Flex>

            {pvz.blockTime && (
              <Text style={{ fontSize: 13, color: colors.red }}>
                {t(getKey('Дата блока'))}: {dayjs(pvz.blockTime).format(DATE_FORMAT_DEFAULT)}
              </Text>
            )}
          </Flex>

          <Flex style={{ minWidth: isTablet ? undefined : 100 }} vertical>
            <Text>
              ID: <Text copyable>{pvz.externalId}</Text>
            </Text>

            <RatingBadge rate={pvz.rate} />
          </Flex>
        </Flex>

        <Flex style={{ width: isTablet ? '100%' : '50%' }} justify="flex-start" align="flex-start">
          <Text copyable>{pvz.address}</Text>
        </Flex>
      </Flex>

      <Flex onClick={(e) => e.stopPropagation()}>
        {!isEmployee && (
          <Dropdown menu={{ items }}>
            <MoreOutlined className={s.menuIcon} />
          </Dropdown>
        )}
      </Flex>
    </ItemBox>
  )
})
